import { useEffect, useState } from 'react';
import { Globe, GraduationCap, Sparkles, type LucideIcon } from 'lucide-react';

import { MediaFrame, ScrollReveal, SplitReveal } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { cn } from '@ibas/ui';
import { brand } from '@/config/brand';

/** Pass through a real /media asset path unchanged. */
const img = (path: string) => path;

interface Panel {
  /** Short, editorial title (font-display). */
  title: string;
  /** One-line caption — institutional facts only. */
  caption: string;
  /** Eyebrow label for the panel. */
  label: string;
  icon: LucideIcon;
  image: string;
  /** Meaningful alt text describing the scene. */
  alt: string;
  /** CSS object-position keeping the subject in frame under object-cover. */
  position: string;
}

/**
 * The five facets of the IBAS experience. Copy is drawn strictly from the
 * institutional brief and content bank (framework: FLEXI · EDU · CARE, plus the
 * 63+ nationalities / 6 continents proof and the Swiss-quality positioning) — no
 * invented facts.
 */
const PANELS: readonly Panel[] = [
  {
    title: 'Flexible study',
    caption: 'Learn online, on-site or blended — study your way.',
    label: 'Flexi',
    icon: Sparkles,
    image: img('/media/images/people/online-class-video-call.webp'),
    alt: 'Students in an IBAS classroom watching a remote instructor via video call, representing flexible online and blended learning.',
    position: '50% 55%',
  },
  {
    title: 'Applied learning',
    caption: 'Career-relevant, interactive study built for real work.',
    label: 'Edu',
    icon: GraduationCap,
    image: img('/media/images/campus/classroom-small-group-discussion.webp'),
    alt: 'An instructor guiding a small group discussion among students in an IBAS classroom.',
    position: '50% 45%',
  },
  {
    title: 'Global community',
    caption: '63+ student nationalities across 6 continents.',
    label: 'Connect',
    icon: Globe,
    image: img('/media/images/graduation-events/graduation-ceremony-group-photo-2024.webp'),
    alt: 'A large, diverse cohort of IBAS graduates in gowns and sashes assembled for a group photograph, reflecting the academy’s global community.',
    position: '50% 45%',
  },
  {
    title: 'Swiss quality',
    caption: 'Recognised, credible qualifications, Swiss-made.',
    label: 'Quality',
    icon: GraduationCap,
    image: img('/media/images/campus/ibas-thalwil-building-exterior.webp'),
    alt: 'Exterior view of the copper-panelled IBAS Thalwil campus building, evoking Swiss educational quality.',
    position: '50% 45%',
  },
  {
    title: 'Support (CARE)',
    caption: 'Guidance and care across your whole journey.',
    label: 'Care',
    icon: Sparkles,
    image: img('/media/images/campus/student-registration-desk.webp'),
    alt: 'Students gathered around the IBAS registration desk with staff assisting them, representing support across the student journey.',
    position: '50% 45%',
  },
];

/** Auto-advance cadence (ms) — motion-only, paused on hover/focus. */
const AUTOPLAY_MS = 4500;

export interface ExperienceHorizontalProps {
  className?: string;
  /** Optional id for in-page anchoring. */
  id?: string;
}

/**
 * ExperienceHorizontal — a cinematic, single-screen facet showcase for "The IBAS
 * experience". (Replaces the previous pinned horizontal-scroll tour: no part of
 * this section translates horizontally on scroll.)
 *
 * Layout: two columns — the header, the five selectable facet chips
 * (Flexi · Edu · Connect · Quality · Care) and the active facet's title + caption
 * sit on the LEFT, over solid navy; a single cinematic media stage in a landscape
 * (4:3) frame sits on the RIGHT. Selecting a chip — by hover, focus or tap —
 * cross-fades both the left copy and the right image to that facet; a gentle
 * auto-advance walks the chips on a timer and pauses the instant the user hovers
 * or focuses the module, so the interaction always feels driven, never stolen.
 * The whole module lives within one viewport (no dead space, no pin).
 *
 * Reduced motion: no auto-advance, no cross-fade transforms and no entrance
 * animation — the first facet renders fully legible and static, and the chips
 * still switch it instantly for anyone who wants to explore.
 */
export function ExperienceHorizontal({ className, id }: ExperienceHorizontalProps) {
  const reduced = useReducedMotion();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  // Gentle auto-advance — only when motion is allowed and the user is not
  // interacting with the module (hover/focus pauses it).
  useEffect(() => {
    if (reduced || paused) return;
    const timer = window.setInterval(() => {
      setActive((i) => (i + 1) % PANELS.length);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [reduced, paused]);

  const select = (index: number) => setActive(index);

  return (
    <section
      id={id}
      className={cn(
        'flex min-h-[100svh] items-center overflow-hidden bg-primary py-14 md:py-20',
        className,
      )}
      aria-label="The IBAS experience"
    >
      {/* Two columns: the text + facet buttons on the left, the cinematic media
          stage on the right. Hover/focus anywhere inside pauses the auto-advance. */}
      <div
        className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-10 px-6 lg:grid-cols-2 lg:gap-14"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        {/* LEFT — header, facet chips, and the active facet's title + caption. */}
        <div>
          <ScrollReveal from="up">
            <Header />
          </ScrollReveal>

          {/* Facet chips — one is always active (gold pill, navy text). */}
          <ScrollReveal
            from="up"
            delay={0.05}
            stagger={0.06}
            className="mt-8 flex flex-wrap gap-2.5 md:mt-10 md:gap-3"
          >
            {PANELS.map((panel, index) => {
              const Icon = panel.icon;
              const isActive = index === active;
              return (
                <button
                  key={panel.title}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => select(index)}
                  onMouseEnter={reduced ? undefined : () => select(index)}
                  onFocus={reduced ? undefined : () => select(index)}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-full border px-4 py-2 font-label text-xs tracking-[0.18em] uppercase transition-colors duration-500 outline-none motion-reduce:transition-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-primary md:text-sm',
                    isActive
                      ? 'border-brand-gold bg-brand-gold text-brand-navy'
                      : 'border-primary-foreground/25 text-primary-foreground/70 hover:border-primary-foreground/50 hover:text-primary-foreground',
                  )}
                >
                  <Icon aria-hidden className="size-4" />
                  {panel.label}
                </button>
              );
            })}
          </ScrollReveal>

          {/* Active facet copy — cross-fades in place on solid navy (no longer
              overlaid on the image). Height reserved so switching never reflows. */}
          <div className="relative mt-8 min-h-[9rem] md:mt-10 md:min-h-[9.5rem]">
            {PANELS.map((panel, index) => {
              const Icon = panel.icon;
              const isActive = index === active;
              return (
                <div
                  key={panel.title}
                  aria-hidden={!isActive}
                  className={cn(
                    'absolute inset-0 transition-opacity duration-700 ease-out motion-reduce:transition-none',
                    isActive
                      ? 'opacity-100'
                      : 'pointer-events-none opacity-0 motion-reduce:hidden',
                  )}
                >
                  <span className="inline-flex items-center gap-2 font-label text-xs tracking-[0.24em] text-brand-gold-light uppercase">
                    <Icon aria-hidden className="size-4" />
                    {panel.label}
                  </span>
                  <h3 className="mt-3 font-display text-3xl leading-[1.05] text-primary-foreground md:text-4xl">
                    {panel.title}
                  </h3>
                  <p className="mt-3 max-w-md text-base text-primary-foreground/80 md:text-lg">
                    {panel.caption}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — cinematic stage in a landscape frame that suits every facet
            photo; facets are stacked and cross-faded by opacity. */}
        <ScrollReveal from="up" delay={0.05}>
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl">
            {PANELS.map((panel, index) => {
              const isActive = index === active;
              return (
                <div
                  key={panel.title}
                  aria-hidden={!isActive}
                  className={cn(
                    'absolute inset-0 transition-[opacity,transform] duration-[900ms] ease-out motion-reduce:transition-none',
                    isActive
                      ? 'opacity-100 scale-100'
                      : 'pointer-events-none scale-[1.04] opacity-0 motion-reduce:hidden motion-reduce:scale-100',
                  )}
                >
                  <MediaFrame
                    image={panel.image}
                    alt={panel.alt}
                    aspect="h-full"
                    rounded="rounded-3xl"
                    reveal={false}
                    overlay={28}
                    position={panel.position}
                    className="h-full w-full"
                  />
                </div>
              );
            })}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

/** Section eyebrow + heading + essence. */
function Header() {
  return (
    <div className="max-w-2xl">
      <p className="font-label text-xs tracking-[0.24em] text-brand-gold uppercase">
        The IBAS experience
      </p>
      <SplitReveal
        as="h2"
        by="lines"
        className="mt-3 font-display text-4xl leading-tight text-primary-foreground md:text-5xl"
      >
        {brand.campaignLine}
      </SplitReveal>
      <p className="mt-4 text-base text-primary-foreground/70 md:text-lg">{brand.essence}</p>
    </div>
  );
}
